import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';

// Context providers
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { SidebarProvider } from './contexts/SidebarContext';

// Components
import NavbarWidget from './components/NavbarWidget';
import SidebarWidget from './components/SidebarWidget';
import FooterWidget from './components/FooterWidget';
import AuthModalWidget from './components/AuthModalWidget';

// Pages
import HomePage from './pages/HomePage';
import ExplorePage from './pages/ExplorePage';
import JobsPage from './pages/JobsPage';
import GigsPage from './pages/GigsPage';
import JobDetailPage from './pages/JobDetailPage';
import GigDetailPage from './pages/GigDetailPage';
import CreateJobPage from './pages/CreateJobPage';
import CreateGigPage from './pages/CreateGigPage';
import DashboardPage from './pages/DashboardPage';
import ChatListPage from './pages/ChatListPage';
import ChatDetailPage from './pages/ChatDetailPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import WalletPage from './pages/WalletPage';
import GovernancePage from './pages/GovernancePage';
import GovernanceLeaderboardPage from './pages/GovernanceLeaderboardPage';
import GovernanceProposalDetailPage from './pages/GovernanceProposalDetailPage';
import ReferralPage from './pages/ReferralPage';
import RegisterPage from './pages/RegisterPage';
import LoginPage from './pages/LoginPage';
import JobApplicationsPage from './pages/JobApplicationsPage';
import GigOrdersPage from './pages/GigOrdersPage';
import EditJobPage from './pages/EditJobPage';
import EditGigPage from './pages/EditGigPage';
import NotificationsPage from './pages/NotificationsPage';
import DeployerPortalPage from './pages/DeployerPortalPage';
import StakingPage from './pages/StakingPage';
import BloggerPage from './pages/BloggerPage';
import CreateBlogPage from './pages/CreateBlogPage';
import EditBlogPage from './pages/EditBlogPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import CommunityGuidelinesPage from './pages/CommunityGuidelinesPage';

// Initialize React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SocketProvider>
          <SidebarProvider>
            <Router>
          <div className="min-h-screen bg-background text-default transition-colors duration-300">
            <div className="flex flex-col lg:flex-row min-h-screen">
              {/* Sidebar */}
              <SidebarWidget />
              
              {/* Main Content Area */}
              <div className="flex-1 lg:ml-64 min-h-screen bg-background flex flex-col">
                {/* Top Navigation */}
                <NavbarWidget />
                
                {/* Main Content */}
                <main className="flex-1 min-h-0 bg-background text-default pb-20 lg:pb-0">
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<HomePage />} />
                  <Route path="/explore" element={<ExplorePage />} />
                  <Route path="/jobs" element={<JobsPage />} />
                  <Route path="/gigs" element={<GigsPage />} />
                  <Route path="/jobs/:id" element={<JobDetailPage />} />
                  <Route path="/gigs/:id" element={<GigDetailPage />} />
                  <Route path="/profile/:username" element={<ProfilePage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/guidelines" element={<CommunityGuidelinesPage />} />
                  
                  {/* Protected Routes */}
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <ProfileRedirect />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <DashboardPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/create-job"
                    element={
                      <ProtectedRoute>
                        <CreateJobPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/create-gig"
                    element={
                      <ProtectedRoute>
                        <CreateGigPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/chats"
                    element={
                      <ProtectedRoute>
                        <ChatListPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/chats/:id"
                    element={
                      <ProtectedRoute>
                        <ChatDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/jobs/:id/applications"
                    element={
                      <ProtectedRoute>
                        <JobApplicationsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/gigs/:id/orders"
                    element={
                      <ProtectedRoute>
                        <GigOrdersPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/jobs/:id/edit"
                    element={
                      <ProtectedRoute>
                        <EditJobPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/gigs/:id/edit"
                    element={
                      <ProtectedRoute>
                        <EditGigPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/notifications"
                    element={
                      <ProtectedRoute>
                        <NotificationsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/wallet"
                    element={
                      <ProtectedRoute>
                        <WalletPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/staking"
                    element={
                      <ProtectedRoute>
                        <StakingPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/blogger"
                    element={
                      <ProtectedRoute>
                        <BloggerPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/create-blog"
                    element={
                      <ProtectedRoute>
                        <CreateBlogPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/edit-blog/:id"
                    element={
                      <ProtectedRoute>
                        <EditBlogPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/governance"
                    element={
                      <ProtectedRoute>
                        <GovernancePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/governance/proposals/:id"
                    element={
                      <ProtectedRoute>
                        <GovernanceProposalDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/governance/leaderboard"
                    element={
                      <ProtectedRoute>
                        <GovernanceLeaderboardPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/referral"
                    element={
                      <ProtectedRoute>
                        <ReferralPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <SettingsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/deployer"
                    element={
                      <ProtectedRoute>
                        <DeployerPortalPage />
                      </ProtectedRoute>
                    }
                  />
                </Routes>
                </main>
              </div>
            </div>
            
            {/* Footer */}
            <FooterWidget />
            
            {/* Auth Modal */}
            <AuthModalWidget />
            
            {/* Toast notifications */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: 'var(--surface-color)',
                  color: 'var(--default-color)',
                  border: '1px solid var(--accent-color)',
                },
              }}
            />
    </div>
            </Router>
          </SidebarProvider>
        </SocketProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-heading text-heading mb-4">
            Please sign in to access this page
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            You need to be authenticated to access this feature.
          </p>
          <AuthModalWidget />
        </div>
      </div>
    );
  }

  return children;
};

// Profile Redirect Component - redirects to user's own profile
const ProfileRedirect = () => {
  const { user } = useAuth();
  
  if (user?.username) {
    return <Navigate to={`/profile/${user.username}`} replace />;
  }
  
  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-center">
        <h1 className="text-2xl font-heading text-heading mb-4">
          Profile not found
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Unable to load your profile. Please try again later.
        </p>
      </div>
    </div>
  );
};

export default App;